import { useMemo } from 'react'
import { Link, NavLink, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { BookOpen, ArrowLeft } from 'lucide-react'
import Card from '@/components/Card'
import { cn } from '@/lib/utils'
import { WIKI_ARTICLES, WIKI_BUILD_TIME, findArticle } from './wikiArticles'

function formatBuildTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Berlin',
    })
  } catch {
    return iso
  }
}

export default function WikiPage() {
  const { slug = 'index' } = useParams()
  const article = useMemo(() => findArticle(slug), [slug])

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-orange-500" />
          <h1 className="text-[22px] font-bold tracking-tight text-[var(--foreground)]">
            Handbuch
          </h1>
        </div>
        <div className="text-xs text-[var(--muted-foreground)]">
          Letzte Aktualisierung: {formatBuildTime(WIKI_BUILD_TIME)}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Artikel-Sidebar */}
        <aside className="col-span-3">
          <Card className="p-3">
            <div className="mb-2 px-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
              Themen
            </div>
            <nav className="space-y-1">
              {WIKI_ARTICLES.map((a) => (
                <NavLink
                  key={a.slug}
                  to={a.slug === 'index' ? '/wiki' : `/wiki/${a.slug}`}
                  end={a.slug === 'index'}
                  className={({ isActive }) =>
                    cn(
                      'block rounded-md px-2 py-1.5 text-[13px] transition-colors',
                      isActive
                        ? 'bg-orange-500/10 font-semibold text-orange-700'
                        : 'text-[var(--foreground)] hover:bg-[var(--muted)]',
                    )
                  }
                >
                  {a.title}
                </NavLink>
              ))}
            </nav>
          </Card>
        </aside>

        {/* Artikel-Inhalt */}
        <main className="col-span-9">
          <Card className="p-6">
            {article ? (
              <article className="wiki-prose">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {article.content}
                </ReactMarkdown>
              </article>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[var(--muted-foreground)]">
                  Artikel „{slug}" nicht gefunden.
                </p>
                <Link
                  to="/wiki"
                  className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Zur Übersicht
                </Link>
              </div>
            )}
          </Card>
        </main>
      </div>
    </div>
  )
}
